import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { IProduct } from "reedy-data";
import { FirestoreGenerationStatus } from "reedy-data/models";
import { ActionCard } from "components/Card/ActionCard";
import { convertToDateStr } from "shared/utils";
import { hasActiveActions } from "utils/status";
import { useNavigate } from "@tanstack/react-router";
import { Modal } from "components/modal/Modal";
import { useManuscriptActions } from "hooks/useManuscriptActions";
import { toast } from "utils/toast";

interface ManuscriptCardProps {
  product: IProduct;
  onDelete: (product: IProduct) => void;
}

export const ManuscriptCard = ({ product, onDelete }: ManuscriptCardProps) => {
  const { t } = useTranslation(["uploadManuscript", "general"]);
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const { generateProductManuscriptAnalysis } = useManuscriptActions();

  // Get the manuscript analysis status and error message
  const manuscriptAnalysisStatus = useMemo(
    () => product?.actions?.manuscript?.actions?.analyse?.status,
    [product],
  );

  const manuscriptAnalysisErrorMessage = useMemo(
    () => product?.actions?.manuscript?.actions?.analyse?.msg,
    [product],
  );

  // Only allow interaction if the manuscript analysis is completed
  const isCompleted = useMemo(
    () => manuscriptAnalysisStatus === FirestoreGenerationStatus.COMPLETED,
    [manuscriptAnalysisStatus],
  );

  // Check if analysis has failed
  const hasError = useMemo(
    () => manuscriptAnalysisStatus === FirestoreGenerationStatus.ERROR,
    [manuscriptAnalysisStatus],
  );

  // Check if any action is in progress (including analysis)
  const isLoading = hasActiveActions(product) || isRetrying;

  // Get the most relevant information to display
  const baseTitle =
    product.manuscript?.filename ||
    product.title ||
    t("uploadManuscript:card.untitled");

  // Create badge for error state
  const badge = hasError
    ? {
        text: t("uploadManuscript:card.errorBadge"),
        variant: "error" as const,
        tooltip:
          manuscriptAnalysisErrorMessage ||
          t("uploadManuscript:card.errorTooltipDefault"),
      }
    : undefined;

  // Format the date safely for any date-like input
  // ToDo: IProduct has a type issue with dates and nested dates
  const uploadDate = convertToDateStr(t, product.manuscript?.uploadedAt);

  // Card navigation handler
  const handleNavigate = () => {
    navigate({
      to: "/manuscripts/$productId",
      params: { productId: product.id },
    });
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(product);
    setIsDeleting(false);
    setShowDeleteDialog(false);
  };

  const handleRetryAnalysis = async () => {
    if (!product.id || isRetrying) return;

    setIsRetrying(true);
    try {
      await generateProductManuscriptAnalysis(product.id);
      toast.success(t("uploadManuscript:toasts.retrySuccess"));
    } catch {
      toast.error(t("uploadManuscript:toasts.retryError"));
    } finally {
      setIsRetrying(false);
    }
  };

  // Determine icon and styling based on state
  const getIconProps = () => {
    if (isLoading) {
      return {
        iconName: "loader" as const,
        iconClassName: "animate-spin",
        iconWrapperClassName: "bg-secondary-25",
      };
    }

    if (hasError) {
      return {
        iconName: "octagon-alert" as const,
        iconClassName: "text-system-error",
        iconWrapperClassName: "bg-system-error/5",
      };
    }

    return {
      iconName: "text-quote" as const,
      iconClassName: "",
      iconWrapperClassName: "bg-secondary-25",
    };
  };

  const iconProps = getIconProps();

  // Determine action button based on state
  const getActions = () => {
    if (isLoading || isRetrying) {
      return [];
    }

    if (hasError) {
      return [
        {
          iconName: "refresh-cw" as const,
          iconClassName: "text-primary hover:text-primary-700",
          label: t("uploadManuscript:card.retryAnalysis"),
          onClick: handleRetryAnalysis,
          disabled: isRetrying,
          testId: "manuscript-card-retry",
        },
        {
          iconName: "trash2" as const,
          iconClassName: "text-system-error hover:text-system-error/80",
          label: t("general:buttons.delete"),
          onClick: () => setShowDeleteDialog(true),
          testId: "manuscript-card-delete",
        },
      ];
    }

    return [
      {
        iconName: "trash2" as const,
        iconClassName: "text-system-error hover:text-system-error/80",
        label: t("general:buttons.delete"),
        onClick: () => setShowDeleteDialog(true),
        testId: "manuscript-card-delete",
      },
    ];
  };

  const actions = getActions();

  return (
    <>
      <ActionCard
        title={baseTitle}
        subtitle={uploadDate}
        iconName={iconProps.iconName}
        iconClassName={iconProps.iconClassName}
        iconWrapperClassName={iconProps.iconWrapperClassName}
        ariaLabel={product.manuscript?.filename}
        onClick={isCompleted ? handleNavigate : undefined}
        actionOnHover
        badge={badge}
      >
        {actions.map((action) => (
          <ActionCard.Action
            key={action.testId || action.iconName}
            iconName={action.iconName}
            iconClassName={action.iconClassName}
            label={action.label}
            onClick={action.onClick}
            disabled={action.disabled}
            testId={action.testId}
          />
        ))}
      </ActionCard>
      <Modal
        id="delete-manuscript"
        open={showDeleteDialog}
        mainIconName="octagon-alert"
        variant="danger"
        title={t("uploadManuscript:card.deleteTitle")}
        description={t("uploadManuscript:card.deleteDescription")}
        confirmLabel={t("uploadManuscript:card.deleteConfirm")}
        confirmIconName="trash2"
        confirmVariant="danger"
        cancelLabel={t("general:buttons.close")}
        onOpenChange={setShowDeleteDialog}
        loading={isDeleting}
        onConfirm={handleDelete}
      />
    </>
  );
};
